/**
 * 音频服务（AudioService）
 * ------------------------------------------------------------
 * 为什么要有这一层：
 *   华为 HarmonyOS / 部分安卓平板上的浏览器对 speechSynthesis 支持很差
 *   （没有英文语音包、getVoices() 永远为空、或者有 voice 但完全不出声）。
 *   只依赖 TTS 的话，用户点半天没有声音，且不知道原因。
 *
 * 策略（按优先级）：
 *   1) HTML5 Audio 播放项目内置 MP3（public/audio/<unit>/<word>.mp3）
 *      —— 最稳，音质固定，不依赖系统语音包
 *   2) MP3 缺失或播放失败 → 回退 speechSynthesis（明确设置 en-US 并检查是否真的有英文 voice）
 *   3) 两条都失败 → 抛出可读的错误原因，由 UI 明确显示（绝不能"点了没反应"）
 *
 * 浏览器自动播放限制：
 *   首次播放必须发生在用户手势里。教材学习页入口的「🔊 开始学习」按钮
 *   会调用 primeAudio() 做一次静音播放"热身"，之后流程内的自动播放才被允许。
 */

/* ------------------------------ 状态 ------------------------------ */
const state = {
  primed: false,          // 是否已在用户手势里完成解锁
  lastError: null,        // 最近一次失败原因（设置页/测试按钮展示）
  playing: null,          // 当前正在播放的 Audio 元素
  lastPlayed: null,       // { text, audio } 最近一次成功发起的内容
};

/** 诊断信息（设置页「测试发音」会展示） */
export function audioDiagnostics() {
  const probe = probeTTS();
  return {
    audioSupported: typeof Audio !== 'undefined',
    ttsSupported: probe.supported,
    englishVoice: probe.englishVoice,
    voiceCount: probe.voiceCount,
    primed: state.primed,
    lastError: state.lastError,
    lastPlayed: state.lastPlayed,
  };
}

/* --------------------------- TTS 能力探测 --------------------------- */
function probeTTS() {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
    return { supported: false, englishVoice: null, voiceCount: 0 };
  }
  let voices = [];
  try { voices = speechSynthesis.getVoices() || []; } catch { /* ignore */ }
  const en = voices.find(v => String(v.lang || '').toLowerCase().startsWith('en'));
  return {
    supported: true,
    voiceCount: voices.length,
    englishVoice: en ? `${en.name} (${en.lang})` : null,
  };
}

/**
 * 检查是否真的有英语 voice。
 * 有些安卓浏览器 getVoices() 首次为空，需要等 voiceschanged 事件。
 * @param {number} timeout 最长等待毫秒
 */
export function waitForEnglishVoice(timeout = 2500) {
  const first = probeTTS();
  if (!first.supported) return Promise.resolve({ ok: false, reason: '浏览器不支持语音合成（speechSynthesis）' });
  if (first.englishVoice) return Promise.resolve({ ok: true, voice: first.englishVoice, list: speechSynthesis.getVoices() });

  return new Promise(resolve => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      const now = probeTTS();
      if (now.englishVoice) resolve({ ok: true, voice: now.englishVoice, list: speechSynthesis.getVoices() });
      else resolve({
        ok: false,
        reason: `系统里没有安装英语语音包（共检测到 ${now.voiceCount} 个语音，其中没有 en-*）`,
      });
    };
    try {
      speechSynthesis.addEventListener?.('voiceschanged', finish, { once: true });
    } catch { /* ignore */ }
    setTimeout(finish, timeout);
  });
}

/* ---------------------------- 解锁（热身） ---------------------------- */
/**
 * 在用户手势里调用一次：解锁 Audio + TTS。
 * 解锁后流程内的自动播放才能发声。
 * @returns {Promise<{ok:boolean, reason?:string}>}
 */
export async function primeAudio() {
  if (state.primed) return { ok: true };

  // 1) 用 WebAudio 创建一个 0 增益的短音，让浏览器把本页标记为"已被用户激活"
  //    （比 data: 的 MP3 更可靠，且不依赖具体音频格式）
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      const c = new AC();
      const osc = c.createOscillator();
      const g = c.createGain();
      g.gain.value = 0.0001;
      osc.connect(g).connect(c.destination);
      osc.start();
      osc.stop(c.currentTime + 0.03);
      if (c.state === 'suspended') await c.resume().catch(() => {});
      setTimeout(() => { try { c.close(); } catch { /* ignore */ } }, 500);
    }
  } catch { /* 热身失败不影响后续 */ }

  // 2) TTS 热身（静音 utterance）
  try {
    if (typeof speechSynthesis !== 'undefined') {
      const warm = new SpeechSynthesisUtterance(' ');
      warm.volume = 0;
      speechSynthesis.speak(warm);
    }
  } catch { /* ignore */ }

  state.primed = true;
  state.lastError = null;
  return { ok: true };
}

/* ----------------------------- MP3 播放 ----------------------------- */
/** 把教材里的相对路径（audio/u1/doctor.mp3）解析成绝对 URL，兼容子目录部署与单文件版 */
export function resolveAudioUrl(rel) {
  if (!rel) return null;
  if (/^(https?:|data:|blob:)/.test(rel)) return rel;
  const base = (typeof document !== 'undefined' && document.baseURI) || location.href;
  return new URL(rel.replace(/^\.?\//, ''), base).href;
}

function playAudioElement(url, { rate = 1 } = {}) {
  return new Promise((resolve, reject) => {
    let a;
    try {
      a = new Audio(url);
    } catch (e) {
      reject(new Error(`无法创建 Audio 对象：${e.message}`));
      return;
    }
    a.preload = 'auto';
    a.playsInline = true;
    a.volume = 1;
    try { a.playbackRate = rate; } catch { /* ignore */ }

    let settled = false;
    const done = (fn, arg) => { if (settled) return; settled = true; cleanup(); fn(arg); };
    const cleanup = () => {
      a.removeEventListener('ended', onEnd);
      a.removeEventListener('error', onErr);
      if (state.playing === a) state.playing = null;
    };
    const onEnd = () => done(resolve, true);
    const onErr = () => {
      const code = a.error?.code;
      const map = {
        1: '播放被中断',
        2: '网络错误，音频文件没取到',
        3: '音频解码失败（文件可能损坏）',
        4: '音频格式不支持或文件不存在',
      };
      done(reject, new Error(`MP3 播放失败：${map[code] || '未知错误'}（${url}）`));
    };
    a.addEventListener('ended', onEnd);
    a.addEventListener('error', onErr);

    // 停止上一个
    stopAudio();
    state.playing = a;

    const p = a.play();
    if (p && typeof p.then === 'function') {
      p.catch(err => {
        const name = err?.name || '';
        if (name === 'NotAllowedError') {
          done(reject, new Error('浏览器拦截了自动播放：请先点一下页面上的「🔊 开始学习 / 测试发音」按钮再试'));
        } else if (name === 'NotSupportedError') {
          done(reject, new Error('浏览器不支持 MP3 格式播放'));
        } else {
          done(reject, new Error(`MP3 播放失败：${err?.message || name || '未知原因'}`));
        }
      });
    }

    // 超时保护：8 秒还没播完也没报错，认为卡住
    setTimeout(() => {
      if (!settled && a.currentTime === 0) {
        done(reject, new Error('音频没有开始播放（可能被静音策略拦住了）'));
      }
    }, 8000);
  });
}

/* ----------------------------- TTS 播放 ----------------------------- */
function speakWithFallback(text, lang = 'en-US', rate = 0.9) {
  return new Promise(async (resolve, reject) => {
    const probe = await waitForEnglishVoice(1800);
    if (!probe.supported) { reject(new Error('浏览器不支持语音合成')); return; }

    const u = new SpeechSynthesisUtterance(text);
    u.lang = lang;                       // 明确设置英语语言环境
    u.rate = rate;
    const enVoice = (probe.list || []).find(v => String(v.lang || '').toLowerCase().startsWith('en'));
    if (enVoice) u.voice = enVoice;
    else if (lang.startsWith('en')) {
      // 没有英文 voice 时强行用默认 voice 读英文，声音会很怪，直接告知用户更诚实
      reject(new Error(`系统没有英语语音包，无法朗读英文（请到系统设置安装英文语音，或使用内置 MP3 音频）`));
      return;
    }

    let settled = false;
    const done = (fn, arg) => { if (settled) return; settled = true; fn(arg); };
    u.onend = () => done(resolve, true);
    u.onerror = e => done(reject, new Error(`语音合成失败（${e?.error || 'unknown'}）`));
    try {
      speechSynthesis.cancel();
      setTimeout(() => { try { speechSynthesis.speak(u); } catch (e) { done(reject, e); } }, 60);
    } catch (e) { done(reject, e); }
    setTimeout(() => done(resolve, true), Math.max(3000, text.length * 120));
  });
}

/* ------------------------------ 对外接口 ------------------------------ */

/** 停止当前正在播放的音频 / 朗读 */
export function stopAudio() {
  if (state.playing) {
    try { state.playing.pause(); state.playing.currentTime = 0; } catch { /* ignore */ }
    state.playing = null;
  }
  try { if (typeof speechSynthesis !== 'undefined') speechSynthesis.cancel(); } catch { /* ignore */ }
}

/**
 * 播放一段音频（MP3 优先）。
 * @param {{audio?:string|null, text?:string, lang?:string}} item
 *        audio 为相对路径；text 是兜底朗读内容
 * @param {{rate?:number, forceTTS?:boolean}} [opts]
 * @returns {Promise<{ok:boolean, via:'mp3'|'tts'|'none', error?:string}>}
 *          永不 reject —— 调用方（教学流程）不需要 try/catch，看返回值即可
 */
export async function playItem(item = {}, opts = {}) {
  const { audio, text, lang = 'en-US' } = item;
  const rate = opts.rate || 1;

  // ① MP3
  if (audio && !opts.forceTTS) {
    const url = resolveAudioUrl(audio);
    try {
      await playAudioElement(url, { rate });
      state.lastPlayed = { text: text || '', audio, at: Date.now() };
      state.lastError = null;
      return { ok: true, via: 'mp3' };
    } catch (mp3Err) {
      // ② TTS 兜底
      if (text) {
        try {
          await speakWithFallback(text, lang, 0.9 * rate);
          state.lastPlayed = { text, audio: null, at: Date.now() };
          state.lastError = `MP3 播放失败，已改用系统语音：${mp3Err.message}`;
          return { ok: true, via: 'tts', error: mp3Err.message };
        } catch (ttsErr) {
          state.lastError = `${mp3Err.message}；系统语音也不可用：${ttsErr.message}`;
          return { ok: false, via: 'none', error: state.lastError };
        }
      }
      state.lastError = mp3Err.message;
      return { ok: false, via: 'none', error: mp3Err.message };
    }
  }

  // 没有 MP3，直接 TTS
  if (text) {
    try {
      await speakWithFallback(text, lang, 0.9 * rate);
      state.lastPlayed = { text, audio: null, at: Date.now() };
      return { ok: true, via: 'tts' };
    } catch (e) {
      state.lastError = e.message;
      return { ok: false, via: 'none', error: e.message };
    }
  }

  state.lastError = '这个单词没有音频文件，也没有可朗读的文本';
  return { ok: false, via: 'none', error: state.lastError };
}

/**
 * 播放一个单词：优先用单词对象自己的 audio 字段。
 * 注意参数必须是**完整的单词对象**（en/zh/audio 在同一个对象里），
 * 不允许传"英文数组 + 音频数组"。
 * @param {{en:string, zh?:string, audio?:string|null}} word
 */
export function playWord(word, opts = {}) {
  if (!word || !word.en) {
    return Promise.resolve({ ok: false, via: 'none', error: '单词数据缺失' });
  }
  return playItem({ audio: word.audio, text: word.en, lang: 'en-US' }, opts);
}

/** 播放一句英文（句子音频暂不生成，走 TTS；这是有意的阶段性取舍） */
export function playSentenceEn(text, opts = {}) {
  return playItem({ audio: null, text, lang: 'en-US' }, opts);
}

/**
 * 「测试发音」：给设置页/学习页用的自检。
 * 依次试 MP3 和 TTS，返回人类可读的结论。
 */
export async function selfTest(sampleWord) {
  const report = { steps: [] };
  await primeAudio();
  report.steps.push(`音频解锁：${state.primed ? '已解锁' : '未解锁'}`);

  const probe = probeTTS();
  report.steps.push(probe.supported
    ? `语音合成：支持，检测到 ${probe.voiceCount} 个语音${probe.englishVoice ? `，英文语音 ${probe.englishVoice}` : '，⚠ 没有英文语音'}`
    : '语音合成：不支持（浏览器没有 speechSynthesis）');

  // MP3 测试
  const word = sampleWord || { en: 'hello', audio: null };
  if (word.audio) {
    const r = await playItem({ audio: word.audio, text: null }, { rate: 1 });
    report.steps.push(r.ok ? `内置音频：播放成功（${word.audio}）` : `内置音频：失败 — ${r.error}`);
  } else {
    report.steps.push('内置音频：当前单词没有 MP3');
  }

  // TTS 测试
  const t = await playItem({ audio: null, text: word.en || 'hello', lang: 'en-US' });
  report.steps.push(t.ok ? '系统语音朗读：成功' : `系统语音朗读：失败 — ${t.error}`);

  report.ok = report.steps.every(s => !/失败/.test(s));
  report.lastError = state.lastError;
  return report;
}
